import * as React from 'react'

import { NetworkStatus } from '@apollo/client'
import { useQuery } from '@apollo/client/react'

import {
  Button,
  CriticalIcon,
  EmptyState,
  Heading,
  Link,
  Paragraph,
  PlusIcon,
  SkeletonText,
  toast,
  UnstyledButton,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { buildTagComparator } from '~publish/components/FilterByTag'
import { useIdeaComposer } from '~publish/components/IdeaManagementRouter'
import {
  useHasOrganizationAction,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'
import { useQueryParam } from '~publish/hooks/useQueryParam'

import { useDeleteIdea } from '../../hooks/useDeleteIdea'
import { CreateUploadDropzone } from '../CreateUploadDropzone'
import {
  getIdeaAccessibleName,
  useSanitizedAndHighlightedText,
} from '../IdeaCard/helpers'
import { IdeaCard_Idea } from '../IdeaCard/fragments/IdeaCard'
import type { IdeaCardIdea } from '../IdeaCard/fragments/IdeaCard'
import { IdeaCardActions } from '../IdeaCard/IdeaCardActions'
import { useMoveIdea } from '../Board/hooks/useMoveIdea'

import styles from './IdeasList.module.css'

import type { JSX } from 'react'

export const GetIdeasList = graphql(
  /* GraphQL */ `
    query GetIdeasList(
      $first: Int!
      $after: String
      $tagsFilter: TagComparator
      $groupFilter: IdeasGroupFilter
      $organizationId: OrganizationId!
    ) {
      ideas(
        input: {
          organizationId: $organizationId
          tagsFilter: $tagsFilter
          groupFilter: $groupFilter
        }
        first: $first
        after: $after
      ) {
        edges {
          node {
            id
            ...IdeaCard_Idea
          }
          cursor
        }
        pageInfo {
          endCursor
          startCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  `,
  [IdeaCard_Idea],
)

const IDEAS_PER_PAGE = 40

const renderRowSkeletons = (length: number): JSX.Element[] => {
  return Array.from({ length }, (_, index) => (
    <div key={index} className={styles.skeletonRow} aria-hidden>
      <SkeletonText lines={1} width="40%" />
    </div>
  ))
}

type IdeaListRowProps = {
  idea: IdeaCardIdea
  onOpen: (idea: IdeaCardIdea) => void
  onDelete: (idea: IdeaCardIdea) => Promise<void>
  onMoveIntoGroup: (id: string, groupId: string | null) => void
}

const IdeaListRow = React.forwardRef<HTMLElement, IdeaListRowProps>(
  ({ idea, onOpen, onDelete, onMoveIntoGroup }, forwardedRef) => {
    const sanitizedText = useSanitizedAndHighlightedText(idea.content.text)
    const ideaName = getIdeaAccessibleName(idea)
    const canManageIdeas = useHasOrganizationAction('manageIdeas')

    const handleDelete = React.useCallback(
      async (): Promise<void> => await onDelete(idea),
      [idea, onDelete],
    )

    const handleMoveIntoGroup = React.useCallback(
      (groupId: string | null): void => {
        onMoveIntoGroup(idea.id, groupId)
      },
      [idea.id, onMoveIntoGroup],
    )

    return (
      <article
        ref={forwardedRef}
        data-testid="idea-list-row"
        data-idea-id={idea.id}
        className={styles.row}
      >
        <div className={styles.rowContent}>
          {idea.content.title ? (
            <Heading
              as="h2"
              size="xsmall"
              className={styles.title}
              translate="no"
            >
              {idea.content.title}
            </Heading>
          ) : (
            // Untitled ideas fall back to a single line of their content
            // so the row is never blank.
            <Paragraph
              className={styles.text}
              translate="no"
              dangerouslySetInnerHTML={{ __html: sanitizedText }}
            />
          )}
        </div>

        {/* this button stretches to cover the entire row and adds accessible focus and click events */}
        <UnstyledButton
          className={styles.overlayButton}
          onClick={(): void => onOpen(idea)}
        >
          <VisuallyHidden>Open idea {ideaName}</VisuallyHidden>
        </UnstyledButton>

        {canManageIdeas && (
          <IdeaCardActions
            idea={idea}
            className={styles.rowActions}
            onDelete={handleDelete}
            onMoveIntoGroup={handleMoveIntoGroup}
          />
        )}
      </article>
    )
  },
)

IdeaListRow.displayName = 'IdeaListRow'

export const IdeasList = ({
  onDrop,
  onPaste,
}: {
  onDrop?: (files: File[], groupId?: string) => void
  onPaste?: (files: File[], groupId?: string) => void
}): JSX.Element => {
  const organizationId = useOrganizationId()
  const [selectedTagIds] = useQueryParam<string[]>('tagIds')
  const [selectedGroupIds] = useQueryParam<string[]>('groupIds')

  const { createIdeaWithComposer, editIdeaWithComposer } = useIdeaComposer()
  const { deleteIdea } = useDeleteIdea()
  const moveIdea = useMoveIdea()

  const { data, loading, error, fetchMore, refetch, networkStatus } = useQuery(
    GetIdeasList,
    {
      variables: {
        first: IDEAS_PER_PAGE,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        organizationId: organizationId!,
        tagsFilter: buildTagComparator(selectedTagIds),
        groupFilter: selectedGroupIds?.length
          ? { groups: selectedGroupIds }
          : undefined,
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      // The fetchMore "loading more" indicator below relies on networkStatus
      // transitioning to fetchMore, so opt in explicitly (default is false).
      notifyOnNetworkStatusChange: true,
      skip: !organizationId,
    },
  )

  const [lastElementRef] = useInfiniteScrollPagination({
    loading: loading || networkStatus === NetworkStatus.fetchMore,
    hasNextPage: data?.ideas.pageInfo.hasNextPage,
    fetchMore: async () => {
      await fetchMore({
        variables: {
          after: data?.ideas.pageInfo.endCursor,
        },
      })
    },
  })

  const handleOpen = React.useCallback(
    (idea: IdeaCardIdea): void => {
      editIdeaWithComposer({
        source: 'create-list-ideaRow-row-1',
        ideaId: idea.id,
      })
    },
    [editIdeaWithComposer],
  )

  const handleDelete = React.useCallback(
    (idea: IdeaCardIdea): Promise<void> =>
      deleteIdea(idea, {
        source: 'create-list-ideaRow-delete-1',
      }),
    [deleteIdea],
  )

  const handleMoveIntoGroup = React.useCallback(
    (ideaId: string, groupId: string | null): void => {
      void (async (): Promise<void> => {
        try {
          await moveIdea({ ideaId, groupId })
          // The group filter is applied server-side, so a move can change
          // whether the idea belongs in the current result set.
          if (selectedGroupIds?.length) {
            await refetch()
          }
        } catch (moveIdeaError: unknown) {
          const message =
            moveIdeaError instanceof Error
              ? moveIdeaError.message
              : 'Please try again later'
          toast.error(`Failed to move idea. ${message}.`)
        }
      })()
    },
    [moveIdea, refetch, selectedGroupIds],
  )

  if (loading && !data) {
    return (
      <div className={styles.wrapper}>
        <VisuallyHidden
          // Loading feedback is a live status because completion is indeterminate.
          // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
          role="status"
        >
          Loading ideas...
        </VisuallyHidden>
        <div className={styles.scrollArea}>
          <div className={styles.feed}>{renderRowSkeletons(8)}</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <EmptyState variant="critical">
        <EmptyState.Icon>
          <CriticalIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Error while loading ideas</EmptyState.Heading>
        <EmptyState.Description>
          Try to refresh the page, contact our team if the problem persists.
        </EmptyState.Description>
      </EmptyState>
    )
  }

  const ideas = data?.ideas?.edges?.map(({ node: idea }) => idea) ?? []
  const hasNextPage = data?.ideas?.pageInfo?.hasNextPage

  if (!ideas.length) {
    return (
      <CreateUploadDropzone onDrop={onDrop} onPaste={onPaste}>
        <EmptyState className={styles.emptyStateWrapper} size="xlarge">
          <EmptyState.Illustration src="https://buffer-publish.s3.amazonaws.com/images/empty-ideas-gallery-illustration.png" />
          <EmptyState.Heading>A space to save your ideas</EmptyState.Heading>
          <EmptyState.Description>
            Create a new Idea or drag and drop images or videos here.
          </EmptyState.Description>
          <EmptyState.Actions>
            <Button
              size="large"
              onClick={(): void =>
                createIdeaWithComposer({
                  source: 'create-list-emptyState-button-1',
                })
              }
            >
              <PlusIcon /> New Idea
            </Button>
          </EmptyState.Actions>
          <Link
            href="https://support.buffer.com/articles/creating-ideas-in-buffer-7gu7n0Hyyq"
            external
          >
            Learn about Ideas
          </Link>
        </EmptyState>
      </CreateUploadDropzone>
    )
  }

  return (
    <CreateUploadDropzone
      className={styles.wrapper}
      onDrop={onDrop}
      onPaste={onPaste}
    >
      <div className={styles.scrollArea}>
        <div
          className={styles.feed}
          role="feed"
          aria-busy={networkStatus === NetworkStatus.fetchMore}
        >
          {ideas.map((idea, index) => (
            <IdeaListRow
              key={idea.id}
              // add ref to last element to trigger pagination
              ref={
                hasNextPage && index === ideas.length - 1
                  ? lastElementRef
                  : null
              }
              idea={idea}
              onOpen={handleOpen}
              onDelete={handleDelete}
              onMoveIntoGroup={handleMoveIntoGroup}
            />
          ))}
          {hasNextPage && renderRowSkeletons(4)}
        </div>
      </div>
    </CreateUploadDropzone>
  )
}

IdeasList.displayName = 'IdeasList'
